import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const ActivityDetailActionsMenu = ({ items = [] }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        buttonRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const updateMenuPosition = () => {
    if (!buttonRef.current || !menuRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuEl = menuRef.current;
    menuEl.style.top = `${rect.bottom + 8}px`;
    menuEl.style.left = `${rect.right}px`;
    menuEl.style.transform = "translateX(-100%)";
    menuEl.style.visibility = "visible";
  };

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    updateMenuPosition();
    const raf = requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  if (!items.length) return null;

  return (
    <>
      <div className="relative shrink-0 flex items-center h-10">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((previous) => !previous)}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          className="flex items-center gap-2 h-10 px-4 rounded-full border border-[#d3d3d3] bg-white hover:bg-[#f8f8f8] transition-colors text-sm font-semibold text-[#222222] cursor-pointer"
        >
          <span>{t("Actions")}</span>
          <i className="fas fa-chevron-down text-xs" aria-hidden />
        </button>
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={t("Actions")}
            className="page-header-actions-menu-portal fixed bg-white rounded-lg border border-[#e0e0e0] py-1 min-w-[240px] max-w-[360px] z-[10000] flex flex-col"
            style={{
              visibility: "hidden",
              top: 0,
              left: 0,
              transformOrigin: "top right",
            }}
          >
            {items.map((item) => (
              <span
                key={item.key || item.label}
                className="block w-full"
                title={item.title}
              >
                <button
                  type="button"
                  role="menuitem"
                  disabled={Boolean(item.disabled)}
                  className={`w-full px-4 py-2.5 text-left text-sm text-[#252733] transition-colors first:rounded-t-lg last:rounded-b-lg border-0 bg-transparent flex items-center gap-2 whitespace-nowrap ${
                    item.disabled
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-[#f5f5f5] cursor-pointer"
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (item.disabled) return;
                    item.onClick?.(event);
                    setIsOpen(false);
                  }}
                >
                  {item.icon && (
                    <span className="shrink-0 w-5 flex items-center justify-center text-[#222222]">
                      {item.icon}
                    </span>
                  )}
                  <span className="min-w-0">{item.label}</span>
                </button>
              </span>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};

export default ActivityDetailActionsMenu;
